/**
 * API client for agent templates and project agents.
 */

import { apiRequest } from "./client";
import type { EffortValue } from "@/lib/model-registry";

/* ---------- Types ---------- */

export interface AdapterConfig {
  model?: string;
  model_policy?: "auto_standard" | "pinned";
  temperature?: number;
  max_tokens?: number;
  thinking_effort?: EffortValue | null;
  /** Set when the user chose the effort; light wakes then keep it. */
  effort_policy?: "pinned";
  max_turns?: number;
  timeout?: number;
  heartbeat_enabled?: boolean;
  heartbeat_interval?: number;
  runtime_source?: string;
  env_vars?: Record<string, string>;
  tools?: string[];
  skills?: string[];
  disabled_apps?: string[];
  capabilities?: string;
}

export interface Skill {
  id: string;
  name: string;
  slug: string;
  description: string;
  is_builtin: boolean;
  skill_type: "builtin" | "custom";
}

export interface CatalogSkill {
  slug: string;
  name: string;
  description: string;
  category: string;
  imported: boolean;
}

export interface AgentTemplate {
  id: string;
  name: string;
  role: string;
  color: string;
  avatar: string;
  avatar_seed: string;
  agent_md_content: string;
  adapter_type: "claude_cli" | "opencode_cli" | "codex_cli" | "api_direct";
  adapter_config: AdapterConfig;
  status: "available" | "disabled";
  is_builtin: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectAgent {
  id: string;
  project: string;
  agent_template: string;
  project_specific_config: Record<string, unknown>;
  template_name: string;
  template_role: string;
  template_color: string;
  template_avatar: string;
  template_avatar_seed: string;
  is_template_builtin: boolean;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  status: "success";
  data: T;
  specialist_creation_enabled?: boolean;
}

/* ---------- Agent Templates ---------- */

export async function listAgentTemplates(
  includeDisabled = false,
  onPolicy?: (enabled: boolean) => void,
): Promise<AgentTemplate[]> {
  const qs = includeDisabled ? "?include_disabled=true" : "";
  const res = await apiRequest<ApiResponse<AgentTemplate[]>>(
    `/api/v1/agent-templates/${qs}`,
  );
  onPolicy?.(res.specialist_creation_enabled ?? true);
  return res.data;
}

export async function getAgentTemplate(
  templateId: string,
): Promise<AgentTemplate> {
  const res = await apiRequest<ApiResponse<AgentTemplate>>(
    `/api/v1/agent-templates/${templateId}/`,
  );
  return res.data;
}

export async function createAgentTemplate(data: {
  name: string;
  role?: string;
  agent_md_content?: string;
  adapter_type?: "claude_cli" | "opencode_cli" | "codex_cli" | "api_direct";
  adapter_config?: AdapterConfig;
}): Promise<AgentTemplate> {
  const res = await apiRequest<ApiResponse<AgentTemplate>>(
    "/api/v1/agent-templates/",
    { method: "POST", body: data },
  );
  return res.data;
}

export async function updateAgentTemplate(
  templateId: string,
  data: Partial<{
    name: string;
    role: string;
    color: string;
    avatar: string;
    avatar_seed: string;
    agent_md_content: string;
    adapter_type: "claude_cli" | "opencode_cli" | "codex_cli" | "api_direct";
    adapter_config: AdapterConfig;
    status: "available" | "disabled";
  }>,
): Promise<AgentTemplate> {
  const res = await apiRequest<ApiResponse<AgentTemplate>>(
    `/api/v1/agent-templates/${templateId}/`,
    { method: "PATCH", body: data },
  );
  return res.data;
}

/* ---------- Project Agents ---------- */

export async function listProjectAgents(
  projectId: string,
  onPolicy?: (enabled: boolean) => void,
): Promise<ProjectAgent[]> {
  const res = await apiRequest<ApiResponse<ProjectAgent[]>>(
    `/api/v1/projects/${projectId}/agents/`,
  );
  onPolicy?.(res.specialist_creation_enabled ?? true);
  return res.data;
}

export async function assignAgentToProject(
  projectId: string,
  agentTemplateId: string,
  config?: Record<string, unknown>,
): Promise<ProjectAgent> {
  const res = await apiRequest<ApiResponse<ProjectAgent>>(
    `/api/v1/projects/${projectId}/agents/`,
    {
      method: "POST",
      body: {
        agent_template: agentTemplateId,
        project_specific_config: config ?? {},
      },
    },
  );
  return res.data;
}

export async function removeAgentFromProject(
  projectId: string,
  agentId: string,
): Promise<void> {
  await apiRequest(`/api/v1/projects/${projectId}/agents/${agentId}/`, {
    method: "DELETE",
  });
}

/* ---------- Skills ---------- */

export async function listSkills(): Promise<Skill[]> {
  const res = await apiRequest<ApiResponse<Skill[]>>("/api/v1/skills/");
  return res.data;
}

export async function listSkillCatalog(): Promise<CatalogSkill[]> {
  const res = await apiRequest<ApiResponse<CatalogSkill[]>>(
    "/api/v1/skills/catalog/",
  );
  return res.data;
}

export async function importSkill(data: {
  slug: string;
  name: string;
  description: string;
}): Promise<Skill> {
  const res = await apiRequest<ApiResponse<Skill>>("/api/v1/skills/", {
    method: "POST",
    body: data,
  });
  return res.data;
}

/* ---------- Heartbeat Toggle ---------- */

export async function toggleHeartbeat(
  enabled: boolean,
): Promise<{ heartbeat_enabled: boolean; agents_updated: number }> {
  const res = await apiRequest<
    ApiResponse<{ heartbeat_enabled: boolean; agents_updated: number }>
  >("/api/v1/agent-templates/heartbeat/", {
    method: "POST",
    body: { enabled },
  });
  return res.data;
}

/* ---------- Heartbeat Suspension ---------- */

/** The slice of the agent runtime-state payload the UI reads. */
export interface AgentRuntimeState {
  id: string;
  project_agent: string;
  runtime_state: string;
  /** Non-null while the rolling-24h heartbeat cap holds this agent. */
  heartbeat_suspended_at: string | null;
}

export async function getAgentRuntimeState(
  projectId: string,
  agentId: string,
): Promise<AgentRuntimeState> {
  const res = await apiRequest<ApiResponse<AgentRuntimeState>>(
    `/api/v1/projects/${projectId}/agents/${agentId}/runtime/`,
  );
  return res.data;
}

/** Resume a heartbeat the rolling-24h cap suspended. The server also
 *  restarts the cap window, so the runs that tripped the breaker cannot
 *  immediately re-trip it. `resumed` is false when it was already running. */
export async function resumeAgentHeartbeat(
  projectId: string,
  agentId: string,
): Promise<{ resumed: boolean }> {
  const res = await apiRequest<ApiResponse<{ resumed: boolean }>>(
    `/api/v1/projects/${projectId}/agents/${agentId}/runtime/resume-heartbeat/`,
    { method: "POST" },
  );
  return res.data;
}

/* ---------- Delete Agent Template ---------- */

export async function deleteAgentTemplate(templateId: string): Promise<void> {
  await apiRequest(`/api/v1/agent-templates/${templateId}/`, {
    method: "DELETE",
  });
}
